/**
 * InjuryPopover (web) — the larger card a player chip in the injury ticker
 * opens on hover, keyboard focus or tap.
 *
 * Rendered into document.body through a portal: the ticker row clips
 * (overflow hidden, 36px tall), so an in-tree dropdown would be cut off.
 * Shows only fields the injury report carries; there is no return date in the
 * feed, so it ends with "Updated <date>".
 */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { View, Text, Image, StyleSheet } from 'react-native';
import { describeInjuryStatus, injuryDetail, formatUpdated } from '../utils/injuryStatus';
import { useReducedMotion } from '../utils/motion';

const WIDTH = 304;
const MARGIN = 12;
const GAP = 8;
const FONT = 'Slate, -apple-system, system-ui, sans-serif';

/** Viewport placement under the chip, clamped to the screen; flips above when needed. */
export function placePopover(rect, viewport, height = 220) {
    const width = Math.min(WIDTH, viewport.width - MARGIN * 2);
    const center = rect.left + rect.width / 2;
    const left = Math.max(MARGIN, Math.min(center - width / 2, viewport.width - width - MARGIN));
    const below = rect.bottom + GAP;
    const flip = below + height > viewport.height && rect.top - GAP - height > MARGIN;
    const top = flip ? rect.top - GAP - height : below;
    const arrowX = Math.max(16, Math.min(center - left, width - 16));
    return { left, top, width, arrowX, flip };
}

const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

function Headshot({ uri, name, ring }) {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [uri]);
    return (
        <View style={[s.headshotRing, { borderColor: ring }]}>
            {uri && !failed ? (
                <Image source={{ uri }} style={s.headshot} onError={() => setFailed(true)} accessibilityIgnoresInvertColors />
            ) : (
                <View style={[s.headshot, s.headshotFallback]}>
                    <Text style={s.initials}>{initials(name)}</Text>
                </View>
            )}
        </View>
    );
}

export default function InjuryPopover({ id, open, player, context, anchorRect, onMouseEnter, onMouseLeave }) {
    const reduced = useReducedMotion();
    const [shown, setShown] = useState(false);
    useEffect(() => {
        if (!open) { setShown(false); return undefined; }
        if (reduced) { setShown(true); return undefined; }
        const raf = requestAnimationFrame(() => setShown(true)); // let the entry transition run
        return () => cancelAnimationFrame(raf);
    }, [open, reduced, player]);

    if (!open || !player || !anchorRect || typeof document === 'undefined') return null;

    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const pos = placePopover(anchorRect, viewport);
    const status = describeInjuryStatus(player.injury_status);
    const detail = injuryDetail(player);
    const meta = [player.team, player.league ? String(player.league).toUpperCase() : null, player.position].filter(Boolean).join(' · ');
    const updated = formatUpdated(detail.updatedAt);

    const node = (
        <View
            id={id}
            role="tooltip"
            style={[
                s.card,
                { left: pos.left, top: pos.top, width: pos.width },
                {
                    opacity: shown ? 1 : 0,
                    transform: [{ translateY: shown || reduced ? 0 : (pos.flip ? 4 : -4) }, { scale: shown || reduced ? 1 : 0.98 }],
                    transitionProperty: 'opacity, transform',
                    transitionDuration: reduced ? '0ms' : '160ms',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                },
            ]}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <View style={[s.accent, { backgroundColor: status.color }]} />
            <View style={[s.arrow, pos.flip ? s.arrowBottom : s.arrowTop, { left: pos.arrowX - 6 }]} />

            {/* Scrolls inside the card on short screens (a fixed card can't be scrolled into view). */}
            <View style={{ maxHeight: Math.max(140, viewport.height - pos.top - MARGIN - 26), overflowY: 'auto' }}>
            <View style={s.header}>
                <Headshot uri={player.headshot_url} name={player.name} ring={status.border} />
                <View style={s.headerText}>
                    <Text style={s.name} numberOfLines={2}>{player.name}</Text>
                    {meta ? <Text style={s.meta} numberOfLines={1}>{meta}</Text> : null}
                    <View style={[s.pill, { backgroundColor: status.bg, borderColor: status.border }]}>
                        <View style={[s.pillDot, { backgroundColor: status.color }]} />
                        <Text style={[s.pillShort, { color: status.color }]}>{status.short}</Text>
                        {status.long.toUpperCase() !== status.short ? (
                            <Text style={s.pillLong} numberOfLines={1}>{status.long}</Text>
                        ) : null}
                    </View>
                </View>
            </View>

            <View style={s.rule} />

            <View style={s.row}>
                <Text style={s.rowLabel}>Injury</Text>
                <View style={s.rowBody}>
                    {detail.headline ? (
                        <Text style={s.rowValue}>{detail.headline}</Text>
                    ) : (
                        <Text style={s.rowMuted}>Not specified in the report</Text>
                    )}
                    {detail.lines.map((line) => (
                        <Text key={line} style={s.rowSub} numberOfLines={4}>{line}</Text>
                    ))}
                </View>
            </View>

            {context ? (
                <View style={s.row}>
                    <Text style={s.rowLabel}>Game</Text>
                    <View style={s.rowBody}>
                        <Text style={s.rowValue} numberOfLines={2}>{context}</Text>
                    </View>
                </View>
            ) : null}

            {updated ? <Text style={s.updated}>{updated}</Text> : null}
            </View>
        </View>
    );

    return createPortal(node, document.body);
}

const s = StyleSheet.create({
    card: {
        position: 'fixed',
        zIndex: 100000,
        paddingTop: 14,
        paddingBottom: 12,
        paddingHorizontal: 14,
        borderRadius: 14,
        backgroundColor: 'rgba(18,18,24,0.98)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.09)',
        boxShadow: '0 18px 48px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.35)',
        overflow: 'visible',
    },
    accent: {
        position: 'absolute',
        top: 0,
        left: 14,
        right: 14,
        height: 2,
        borderBottomLeftRadius: 2,
        borderBottomRightRadius: 2,
        opacity: 0.85,
    },
    arrow: {
        position: 'absolute',
        width: 12,
        height: 12,
        backgroundColor: 'rgba(18,18,24,0.98)',
        borderColor: 'rgba(255,255,255,0.09)',
        transform: [{ rotate: '45deg' }],
    },
    arrowTop: { top: -6.5, borderTopWidth: 1, borderLeftWidth: 1 },
    arrowBottom: { bottom: -6.5, borderBottomWidth: 1, borderRightWidth: 1 },
    header: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    headshotRing: {
        width: 60,
        height: 60,
        borderRadius: 30,
        borderWidth: 2,
        padding: 2,
        flexShrink: 0,
    },
    headshot: { width: '100%', height: '100%', borderRadius: 26, backgroundColor: 'rgba(255,255,255,0.06)' },
    headshotFallback: { alignItems: 'center', justifyContent: 'center' },
    initials: { color: 'rgba(255,255,255,0.7)', fontSize: 17, fontWeight: '700', fontFamily: FONT },
    headerText: { flex: 1, minWidth: 0, gap: 3 },
    name: { color: '#fff', fontSize: 16, lineHeight: 19, fontWeight: '700', letterSpacing: -0.2, fontFamily: FONT },
    meta: { color: 'rgba(255,255,255,0.5)', fontSize: 11.5, fontWeight: '500', fontFamily: FONT },
    pill: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 6,
        marginTop: 3,
        paddingVertical: 3,
        paddingLeft: 7,
        paddingRight: 9,
        borderRadius: 999,
        borderWidth: 1,
        maxWidth: '100%',
    },
    pillDot: { width: 6, height: 6, borderRadius: 3 },
    pillShort: { fontSize: 10.5, fontWeight: '800', letterSpacing: 0.5, fontFamily: FONT },
    pillLong: { color: 'rgba(255,255,255,0.75)', fontSize: 11, fontWeight: '500', fontFamily: FONT, flexShrink: 1 },
    rule: { height: 1, backgroundColor: 'rgba(255,255,255,0.07)', marginVertical: 12 },
    row: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 8 },
    rowLabel: {
        width: 46,
        paddingTop: 2,
        color: 'rgba(255,255,255,0.38)',
        fontSize: 9.5,
        fontWeight: '700',
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        fontFamily: FONT,
    },
    rowBody: { flex: 1, minWidth: 0, gap: 2 },
    rowValue: { color: 'rgba(255,255,255,0.92)', fontSize: 13, fontWeight: '600', lineHeight: 17, fontFamily: FONT },
    rowSub: { color: 'rgba(255,255,255,0.6)', fontSize: 12, lineHeight: 16, fontFamily: FONT },
    rowMuted: { color: 'rgba(255,255,255,0.4)', fontSize: 12.5, fontStyle: 'italic', fontFamily: FONT },
    updated: { marginTop: 2, color: 'rgba(255,255,255,0.35)', fontSize: 10.5, fontWeight: '500', fontFamily: FONT },
});
